import type { MedplumClient } from '@medplum/core';
import type { Reference } from '@medplum/fhirtypes';
import {
  createEmptyClientThread,
  findClientThreadForPatient,
} from './messagingFhir';
import { resolveMessagingCurrentUserRef } from './messagingIdentity';

type ProfileLike = { resourceType: string; id?: string; name?: unknown };

/**
 * Find an existing provider↔client thread or create an empty one (Group + thread root, no messages).
 */
export async function ensureClientMessageThread(
  medplum: MedplumClient,
  profile: ProfileLike,
  patientId: string,
  patientDisplay: string,
): Promise<string> {
  const senderRef = resolveMessagingCurrentUserRef(profile, false);
  const userReference = senderRef.reference;
  if (!userReference) {
    throw new Error('Unable to resolve your messaging identity');
  }

  const existing = await findClientThreadForPatient(medplum, userReference, patientId);
  if (existing?.id) {
    return existing.id;
  }

  const patientRef: Reference = {
    reference: `Patient/${patientId}`,
    display: patientDisplay,
  };

  const thread = await createEmptyClientThread(medplum, { senderRef, counterpartyRef: patientRef });
  if (!thread.id) {
    throw new Error('Failed to create message thread');
  }
  medplum.invalidateSearches('Group');
  medplum.invalidateSearches('Communication');
  return thread.id;
}
